<template>
  <div class="dp-form" v-if="model">
    <!-- 1. 작업시간 (주간/연장/야간) -->
    <div class="form-group row">
      <label class="col-2 form-control-label"><span>작업시간</span></label>
      <div class="col-10 pt-2">
        <span class="dp-time-range">
          <select class="form-control dp-time-input" v-model="model.workFromTime">
            <option value="">--:--</option>
            <option v-for="t in timeOptions30" :key="'from-'+t" :value="t">{{ t }}</option>
          </select>
          <span class="mh-5">~</span>
          <select class="form-control dp-time-input" v-model="model.workToTime">
            <option value="">--:--</option>
            <option v-for="t in timeOptions30" :key="'to-'+t" :value="t">{{ t }}</option>
          </select>
        </span>
        <span class="dp-time-slots">
          <label class="md-check mr-20" v-for="slot in timeSlotOptions" :key="'ts-'+slot.value">
            <input type="checkbox" v-model="model.timeSlots" :value="slot.value">
            <i class="blue"></i>{{ slot.label }}
          </label>
        </span>
      </div>
    </div>

    <!-- 2. 기본 정보 (부모 prefill, 수정 가능 / 변경 시 사유 필수) -->
    <div class="form-group row mt-10">
      <label class="col-2 form-control-label"><span>기본 정보</span></label>
      <div class="col-10 pt-2">
        <table class="tbl dp-form-tbl">
          <colgroup><col width="120px"><col width="auto"><col width="120px"><col width="auto"></colgroup>
          <tbody>
            <tr>
              <th>작업명</th>
              <td><input type="text" class="form-control" v-model="model.basic.planName"></td>
              <th>업체명</th>
              <td><input type="text" class="form-control" v-model="model.basic.companyName"></td>
            </tr>
            <tr>
              <th>작업책임자</th>
              <td><input type="text" class="form-control" v-model="model.basic.workManager"></td>
              <th>책임자 연락처</th>
              <td><input type="text" class="form-control" v-model="model.basic.workManagerPhone"></td>
            </tr>
            <tr>
              <th>안전담당자</th>
              <td><input type="text" class="form-control" v-model="model.basic.safetyManager"></td>
              <th>안전담당자 연락처</th>
              <td><input type="text" class="form-control" v-model="model.basic.safetyManagerPhone"></td>
            </tr>
            <tr>
              <th>작업인원</th>
              <td><input type="number" min="0" class="form-control" v-model.number="model.basic.workPeopleCount"></td>
              <th>작업기간</th>
              <td>
                <input type="date" class="form-control dp-date-inline" v-model="fromDateStr">
                <span class="mh-5">~</span>
                <input type="date" class="form-control dp-date-inline" v-model="toDateStr">
              </td>
            </tr>
          </tbody>
        </table>

        <!-- 작업위치 (부모 안전작업허가서와 동일한 트리 선택) -->
        <div class="dp-sub-label mt-15">작업위치</div>
        <table class="tbl dp-loc-tbl">
          <colgroup><col width="140px"><col width="140px"><col width="110px"><col></colgroup>
          <thead>
            <tr><th>구분</th><th>관</th><th>층</th><th>상세위치</th></tr>
          </thead>
          <tbody v-if="!regionGroups.length">
            <tr><td colspan="4" class="text-center text-888">위치 데이터가 없습니다.</td></tr>
          </tbody>
          <tbody v-else>
            <template v-for="region in regionGroups">
              <template v-for="building in region.buildings">
                <tr v-for="(row, rowIndex) in building.rows" :key="region.key+'|'+building.key+'|'+row.key">
                  <td v-if="building.isFirst && rowIndex === 0" :rowspan="region.rowspan">{{ region.regionName }}</td>
                  <td v-if="rowIndex === 0" :rowspan="building.rows.length">{{ building.buildingName }}</td>
                  <td>
                    <label v-if="row.details && row.details.length" class="md-check" title="이 층 상세위치 전체 선택/해제">
                      <input type="checkbox" :checked="isFloorAllSelected(row)" @change="toggleFloorAll(row)">
                      <i class="blue"></i>{{ row.floorName }}
                    </label>
                    <span v-else>{{ row.floorName }}</span>
                  </td>
                  <td>
                    <template v-if="row.details && row.details.length">
                      <label class="md-check mr-15" v-for="detail in row.details" :key="detail.key" style="margin-bottom:4px;">
                        <input type="checkbox" v-model="model.basic.selectedFloorKeys" :value="detail.key">
                        <i class="blue"></i>{{ detail.detailName }}
                      </label>
                    </template>
                    <span v-else class="text-888">-</span>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>

        <!-- 선택 위치별 상세위치 텍스트 -->
        <div v-if="selectedLocations.length" class="mt-10">
          <div class="dp-sub-label">상세위치 (선택 위치별)</div>
          <table class="tbl dp-locdetail-tbl">
            <colgroup><col><col width="55%"></colgroup>
            <tbody>
              <tr v-for="loc in selectedLocations" :key="'ld-'+loc.key">
                <th class="dp-locdetail-th">{{ loc.label }}</th>
                <td>
                  <input type="text" class="form-control"
                         :value="model.locationDetails[loc.key] || ''"
                         @input="setLocationDetail(loc.key, $event.target.value)"
                         placeholder="상세위치 입력">
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="basicChanged" class="dp-reason">
          <span class="dp-reason-label ess">수정사유</span>
          <input type="text" class="form-control" :class="{ 'dp-reason-empty': !model.basicReason }"
                 v-model="model.basicReason" placeholder="부모 안전작업허가서와 다르게 수정한 사유를 입력하세요">
        </div>
      </div>
    </div>

    <!-- 3. 고위험작업허가대상 (상단 일일작업 신청 매트릭스 선택이 원본 — 여기서는 수정 불가) -->
    <div class="form-group row mt-10">
      <label class="col-2 form-control-label"><span>고위험작업허가대상</span></label>
      <div class="col-10 pt-2">
        <div class="text-888 mb-5" style="font-size:11px;">
          상단 「일일작업 신청」에서 이 날짜에 체크한 작업이 자동 반영됩니다.
          변경하려면 상단 매트릭스에서 체크를 수정한 뒤 「작성」을 다시 눌러 주세요.
        </div>
        <div class="dp-risk-grid">
          <label v-for="t in highTypes" :key="'hr-'+t.key" class="md-check dp-risk-item dp-risk-fixed">
            <input type="checkbox" :value="t.label" v-model="model.highRiskTargets" disabled>
            <i class="blue"></i>
            <span>{{ t.label }}</span>
            <span class="tag ml-5 light-blue" style="font-size:9px;">고위험</span>
          </label>
          <label v-for="t in heavyTypes" :key="'hv-'+t.key" class="md-check dp-risk-item dp-risk-fixed">
            <input type="checkbox" :value="t.label" v-model="model.heavyWorkTypes" disabled>
            <i class="blue"></i>
            <span>{{ t.label }}</span>
            <span class="tag ml-5 warning" style="font-size:9px;">중장비</span>
          </label>
        </div>
      </div>
    </div>

    <!-- 4. 보호구 (부모 위험성평가에서 집계, 수정 가능 / 변경 시 사유 필수) -->
    <div class="form-group row mt-10">
      <label class="col-2 form-control-label"><span>보호구</span></label>
      <div class="col-10 pt-2">
        <div class="dp-prot-list">
          <span class="dp-prot-chip" v-for="(p, i) in model.protections" :key="'prot-'+(p.key || i)">
            {{ protLabel(p) }}
            <i class="dp-prot-x" @click="removeProtection(i)">×</i>
          </span>
          <span v-if="!model.protections.length" class="text-888" style="font-size:12px;">선택된 보호구가 없습니다.</span>
        </div>
        <button type="button" class="btn btn-xs light-blue mt-5" @click="openProtectionModal">보호구 선택</button>
        <div v-if="protectionsChanged" class="dp-reason">
          <span class="dp-reason-label ess">수정사유</span>
          <input type="text" class="form-control" :class="{ 'dp-reason-empty': !model.protectionReason }"
                 v-model="model.protectionReason" placeholder="보호구를 부모와 다르게 수정한 사유를 입력하세요">
        </div>
      </div>
    </div>

    <!-- 5. 작업 위험성 평가 (부모 출력, 수정 가능 / 변경 시 사유 필수) -->
    <div class="form-group row mt-10">
      <label class="col-2 form-control-label"><span>작업 위험성 평가</span></label>
      <div class="col-10 pt-2">
        <div class="btn-wrap mb-10">
          <button type="button" class="btn btn-md light-blue" @click="addRiskRow">
            <i class="icon-plus mr-5"></i>행 추가
          </button>
          <button type="button" class="btn btn-md line-2" @click="removeRiskRows" :disabled="!hasRiskSelected">
            <i class="icon-minus mr-5"></i>행 삭제
          </button>
        </div>
        <div class="dp-risk-tbl-wrap">
          <table class="tbl select-tbl dp-risk-tbl">
            <colgroup>
              <col width="50px">
              <col width="170px">
              <col width="180px">
              <col width="180px">
              <col width="180px">
              <col width="180px">
              <col width="200px">
              <col width="100px">
              <col width="200px">
              <col width="110px">
            </colgroup>
            <thead>
              <tr>
                <th>
                  <label class="md-check">
                    <input type="checkbox" v-model="riskCheckAll">
                    <i class="blue"></i>
                  </label>
                </th>
                <th>작업항목</th><th>작업절차</th><th>위험요인</th><th>보호구</th>
                <th>측정·장비</th><th>현재 안전조치</th><th>위험성</th><th>추가 안전조치</th><th></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(row, ri) in model.riskRows">
                <tr v-for="(set, si) in riskSets(row)" :key="'rr-'+ri+'-'+si">
                  <td v-if="si === 0" :rowspan="row.sets.length" style="vertical-align:middle; text-align:center;">
                    <label class="md-check">
                      <input type="checkbox" v-model="row.checked">
                      <i class="blue"></i>
                    </label>
                  </td>
                  <td v-if="si === 0" :rowspan="row.sets.length" style="vertical-align:middle;">
                    <div class="td-box">
                      <input type="text" class="form-control" v-model="row.item">
                    </div>
                  </td>
                  <td>
                    <div class="td-box">
                      <input type="text" class="form-control" v-model="set.procedure">
                    </div>
                  </td>
                  <td>
                    <div class="td-box">
                      <input type="text" class="form-control" v-model="set.hazard">
                    </div>
                  </td>
                  <td>
                    <div class="td-box">
                      <input type="text" class="form-control" v-model="set.protection">
                    </div>
                  </td>
                  <td>
                    <div class="td-box">
                      <input type="text" class="form-control" v-model="set.tool">
                    </div>
                  </td>
                  <td>
                    <div class="td-box">
                      <input type="text" class="form-control" v-model="set.currentSafety">
                    </div>
                  </td>
                  <td style="vertical-align:middle;">
                    <div class="td-box dp-risk-level">
                      <Dropdown v-model="set.riskLevel"
                                :datasource="riskLevelOptions"
                                displayName="label"
                                valueName="value"
                                :isSelectionFirstData="false"
                                :width="70"/>
                      <span v-if="set.riskLevel" class="wp-risk-badge" :class="'wp-risk-badge-' + getRiskLevelKey(set.riskLevel)">{{ set.riskLevel }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="td-box">
                      <input type="text" class="form-control" v-model="set.additionalSafety">
                    </div>
                  </td>
                  <td class="dp-risk-actions">
                    <button v-if="si === 0" type="button" class="btn btn-xs light-blue mr-3" @click="addRiskSet(row)">추가</button>
                    <button type="button" class="btn btn-xs line-2" @click="removeRiskSet(row, si)" :disabled="row.sets.length === 1">삭제</button>
                  </td>
                </tr>
              </template>
              <tr v-if="!model.riskRows.length">
                <td colspan="10" class="text-center text-888">부모 안전작업허가서에 위험성평가 내용이 없습니다.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="riskChanged" class="dp-reason">
          <span class="dp-reason-label ess">수정사유</span>
          <input type="text" class="form-control" :class="{ 'dp-reason-empty': !model.riskReason }"
                 v-model="model.riskReason" placeholder="위험성평가를 부모와 다르게 수정한 사유를 입력하세요">
        </div>
      </div>
    </div>

    <!-- 6. 작업자 명단 (작업인원 수만큼, 인쇄 후 서명) -->
    <div class="form-group row mt-10">
      <label class="col-2 form-control-label"><span>작업자 명단</span></label>
      <div class="col-10 pt-2">
        <span class="text-888" style="font-size:11px;">
          작업인원({{ model.basic.workPeopleCount || 0 }}명) 수만큼 행이 생성됩니다. 인쇄 후 각 작업자가 서명란에 서명합니다.
        </span>
        <table class="tbl dp-worker-tbl mt-5">
          <colgroup>
            <col width="55px"><col width="auto"><col width="160px">
            <col width="55px"><col width="auto"><col width="160px">
          </colgroup>
          <thead>
            <tr><th>순번</th><th>성명</th><th>서명</th><th>순번</th><th>성명</th><th>서명</th></tr>
          </thead>
          <tbody>
            <tr v-for="(pair, pi) in workerPairs" :key="'wp-'+pi">
              <td class="text-center">{{ pair[0].idx + 1 }}</td>
              <td><input type="text" class="form-control" v-model="pair[0].w.name"></td>
              <td class="dp-sign-cell"></td>
              <template v-if="pair[1]">
                <td class="text-center">{{ pair[1].idx + 1 }}</td>
                <td><input type="text" class="form-control" v-model="pair[1].w.name"></td>
                <td class="dp-sign-cell"></td>
              </template>
              <template v-else>
                <td class="dp-worker-empty"></td><td class="dp-worker-empty"></td><td class="dp-worker-empty"></td>
              </template>
            </tr>
            <tr v-if="!model.workers.length">
              <td colspan="6" class="text-center text-888">작업인원 수를 입력하면 명단이 생성됩니다.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { PROTECTION_OPTIONS, protectionLabel } from './protectionOptions';
import Dropdown from '@/components/Dropdown.vue';

export default {
  name: 'DailyPermitForm',
  components: {
    Dropdown
  },
  props: {
    // 일일허가서 공통 양식 모델 (DailyPermitInput의 tab.common). 직접 변경한다.
    model: {
      type: Object,
      required: true
    },
    // 부모가 신청한 위험작업 종류 (고위험대상 체크박스 옵션)
    availableTypes: {
      type: Array,
      default: () => []
    },
    // 작업위치 선택 트리 (부모 IDC 기준, DailyPermitInput.regionGroups)
    regionGroups: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    'model.basic.workPeopleCount'(n) {
      this.syncWorkers(n);
    }
  },
  data() {
    return {
      timeSlotOptions: [
        { value: '주간', label: '주간 (09:00~18:00)' },
        { value: '연장', label: '연장 (18:00~24:00)' },
        { value: '야간', label: '야간 (24:00~09:00)' }
      ],
      riskLevelOptions: [
        { label: '상', value: '상' },
        { label: '중', value: '중' },
        { label: '하', value: '하' }
      ]
    };
  },
  computed: {
    // 작업시간 선택 옵션: 30분 단위 (00:00 ~ 23:30)
    timeOptions30() {
      const out = [];
      for (let h = 0; h < 24; h += 1) {
        for (let m = 0; m < 60; m += 30) {
          out.push(String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0'));
        }
      }
      return out;
    },
    highTypes() {
      return this.availableTypes.filter((t) => t.kind === 'high');
    },
    heavyTypes() {
      return this.availableTypes.filter((t) => t.kind === 'heavy');
    },
    // 선택된 작업위치 목록 (key + 표시 라벨) — 상세위치 입력 행 생성용
    selectedLocations() {
      const keys = this.model.basic.selectedFloorKeys || [];
      const out = [];
      this.regionGroups.forEach((region) => {
        region.buildings.forEach((building) => {
          building.rows.forEach((row) => {
            (row.details || []).forEach((detail) => {
              if (keys.includes(detail.key)) {
                out.push({
                  key: detail.key,
                  label: `${region.regionName} | ${building.buildingName} | ${row.floorName} | ${detail.detailName}`
                });
              }
            });
          });
        });
      });
      return out;
    },
    // 작업자 명단을 (순번·성명·서명) 2쌍씩 한 행에 배치하기 위한 페어 묶음
    workerPairs() {
      const pairs = [];
      const ws = this.model.workers || [];
      for (let i = 0; i < ws.length; i += 2) {
        pairs.push([
          { w: ws[i], idx: i },
          ws[i + 1] ? { w: ws[i + 1], idx: i + 1 } : null
        ]);
      }
      return pairs;
    },
    fromDateStr: {
      get() { return this.tsToStr(this.model.basic.fromDate); },
      set(v) { this.model.basic.fromDate = this.strToTs(v); }
    },
    toDateStr: {
      get() { return this.tsToStr(this.model.basic.toDate); },
      set(v) { this.model.basic.toDate = this.strToTs(v); }
    },
    riskCheckAll: {
      get() {
        return this.model.riskRows.length > 0 && this.model.riskRows.every((row) => row.checked);
      },
      set(v) {
        this.model.riskRows.forEach((row) => {
          this.$set(row, 'checked', !!v);
        });
      }
    },
    hasRiskSelected() {
      return this.model.riskRows.some((row) => row.checked);
    },
    basicChanged() {
      return JSON.stringify(this.model.basic) !== JSON.stringify(this.model._orig.basic);
    },
    protectionsChanged() {
      return JSON.stringify(this.model.protections) !== JSON.stringify(this.model._orig.protections);
    },
    riskChanged() {
      return JSON.stringify(this.cleanRiskRows(this.model.riskRows)) !== JSON.stringify(this.cleanRiskRows(this.model._orig.riskRows));
    }
  },
  methods: {
    // 작업시간: 분을 30분 단위로 스냅(직접 입력 대비). 45분↑ → 다음 시간 정각.
    snapTimeTo30(field) {
      const v = this.model[field];
      const m = /^(\d{1,2}):(\d{2})/.exec(v || '');
      if (!m) return;
      let h = parseInt(m[1], 10);
      const snapped = Math.round(parseInt(m[2], 10) / 30) * 30;
      let min = snapped;
      if (snapped >= 60) { min = 0; h = (h + 1) % 24; }
      const nv = String(h).padStart(2, '0') + ':' + String(min).padStart(2, '0');
      if (nv !== v) this.model[field] = nv;
    },
    // 위치선택: 해당 층 상세위치 전체 선택됨 여부 / 전체 선택·해제 토글
    isFloorAllSelected(row) {
      const keys = (this.model.basic.selectedFloorKeys) || [];
      if (!row || !row.details || !row.details.length) return false;
      return row.details.every((d) => keys.includes(d.key));
    },
    toggleFloorAll(row) {
      if (!row || !row.details || !row.details.length) return;
      const keys = row.details.map((d) => d.key);
      const cur = this.model.basic.selectedFloorKeys || [];
      if (this.isFloorAllSelected(row)) {
        this.model.basic.selectedFloorKeys = cur.filter((k) => !keys.includes(k));
      } else {
        const set = new Set(cur);
        keys.forEach((k) => set.add(k));
        this.model.basic.selectedFloorKeys = Array.from(set);
      }
    },
    tsToStr(ts) {
      if (ts == null || ts === '') return '';
      const d = new Date(Number(ts));
      if (Number.isNaN(d.getTime())) return '';
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    strToTs(str) {
      if (!str) return null;
      const parts = str.split('-');
      if (parts.length !== 3) return null;
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    },
    protLabel(option) {
      return protectionLabel(option);
    },
    // 위험성 수준 → 배지 색상 키 (안전작업허가서와 동일)
    getRiskLevelKey(level) {
      if (level === '상') return 'high';
      if (level === '중') return 'mid';
      if (level === '하') return 'low';
      return '';
    },
    // 선택 위치별 상세위치 입력 (Vue2 반응성 위해 $set)
    setLocationDetail(key, value) {
      this.$set(this.model.locationDetails, key, value);
    },
    // 작업인원 수에 맞춰 작업자 명단 행을 조정 (입력값은 보존, 뒤에서 가감)
    syncWorkers(count) {
      const c = Math.max(0, Math.min(parseInt(count, 10) || 0, 300));
      const arr = this.model.workers;
      while (arr.length < c) arr.push({ name: '' });
      while (arr.length > c) arr.pop();
    },
    removeProtection(i) {
      this.model.protections.splice(i, 1);
    },
    cleanRiskRows(rows) {
      return (rows || []).map((row) => ({
        item: row.item || '',
        startDate: row.startDate || '',
        endDate: row.endDate || '',
        sets: (row.sets || []).map((set) => ({
          procedure: set.procedure || '',
          hazard: set.hazard || '',
          protection: set.protection || '',
          tool: set.tool || '',
          currentSafety: set.currentSafety || '',
          riskLevel: set.riskLevel || '',
          additionalSafety: set.additionalSafety || ''
        }))
      }));
    },
    createRiskSet() {
      return {
        procedure: '',
        hazard: '',
        protection: '',
        tool: '',
        currentSafety: '',
        riskLevel: '',
        additionalSafety: ''
      };
    },
    createRiskRow() {
      return {
        checked: false,
        item: '',
        sets: [this.createRiskSet()]
      };
    },
    riskSets(row) {
      if (!row.sets || !row.sets.length) this.$set(row, 'sets', [this.createRiskSet()]);
      return row.sets;
    },
    addRiskRow() {
      this.model.riskRows.push(this.createRiskRow());
    },
    removeRiskRows() {
      this.model.riskRows = this.model.riskRows.filter((row) => !row.checked);
    },
    addRiskSet(row) {
      if (!row.sets) this.$set(row, 'sets', []);
      row.sets.push(this.createRiskSet());
    },
    removeRiskSet(row, setIndex) {
      if (!row || !row.sets || row.sets.length <= 1) return;
      row.sets.splice(setIndex, 1);
    },
    // 부모와 동일한 보호구 선택 모달(WorkPermitSelect) 호출
    openProtectionModal() {
      this.openModal({
        view: 'modal/WorkPermitSelect',
        side: false,
        title: '보호구 선택',
        showBottom: false,
        props: {
          options: PROTECTION_OPTIONS,
          selected: this.model.protections,
          multiple: true,
          useCheckbox: true,
          nameLabel: '보호구',
          descriptionLabel: '내용 설명',
          columns: [
            { label: '구분', key: 'category', width: '180px' },
            { label: '보호구명', key: 'name' }
          ],
          valueKey: 'key',
          emptyText: '목록이 없습니다.',
          onSelect: (selected) => {
            const list = Array.isArray(selected) ? selected : (selected ? [selected] : []);
            // 프롭 배열 참조 유지 위해 splice 로 교체
            this.model.protections.splice(0, this.model.protections.length, ...list);
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.dp-form-tbl th {
  background: #f7f8fa;
  font-weight: 600;
  font-size: 12px;
  color: #555;
  text-align: left;
  padding: 6px 10px;
}
.dp-form-tbl td {
  padding: 4px 8px;
}
.dp-form-tbl .form-control {
  font-size: 12px;
}
.dp-date-inline {
  display: inline-block;
  width: 140px;
}
.dp-sub-label {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 6px;
}
.dp-loc-tbl th,
.dp-loc-tbl td {
  font-size: 12px;
  padding: 6px 8px;
  vertical-align: middle;
}
.dp-loc-tbl thead th {
  background: #f7f8fa;
  text-align: center;
}
.dp-locdetail-tbl th.dp-locdetail-th {
  background: #f7f8fa;
  font-size: 12px;
  font-weight: 500;
  color: #444;
  text-align: left;
  padding: 6px 10px;
}
.dp-locdetail-tbl td {
  padding: 4px 8px;
}
.dp-locdetail-tbl .form-control {
  font-size: 12px;
}
.dp-time-range {
  display: inline-flex;
  align-items: center;
}
.dp-time-slots {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding-left: 16px;
  border-left: 1px solid #e2e2e2;
}
.dp-time-input {
  display: inline-block;
  width: 120px;
  font-size: 12px;
}
.mh-5 { margin: 0 5px; }
.dp-form .btn {
  border-radius: 2px;
}
.dp-risk-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
}
.dp-risk-item {
  display: inline-flex !important;
  align-items: center;
}
/* 매트릭스 선택이 원본이라 여기서는 읽기 전용 (체크 상태만 표시) */
.dp-risk-fixed {
  cursor: default;
}
.dp-risk-fixed input[disabled] + i.blue {
  opacity: .75;
}
.dp-prot-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
  min-height: 24px;
  align-items: center;
}
.dp-prot-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  background: #eef3fb;
  border: 1px solid #c9dcf5;
  border-radius: 12px;
  font-size: 12px;
  color: #1565c0;
}
.dp-prot-x {
  margin-left: 6px;
  font-style: normal;
  font-size: 13px;
  line-height: 1;
  color: #7aa3d8;
  cursor: pointer;
}
.dp-prot-x:hover {
  color: #d9534f;
}
.dp-risk-tbl-wrap {
  overflow-x: auto;
}
.dp-risk-tbl {
  min-width: 1100px;
}
.dp-risk-tbl th {
  background: #f7f8fa;
  font-size: 12px;
  padding: 6px 4px;
  text-align: center;
}
.dp-risk-tbl td {
  padding: 3px 4px;
}
.dp-risk-tbl .form-control {
  font-size: 12px;
}
.dp-risk-level {
  display: flex;
  align-items: center;
  gap: 6px;
}
.dp-risk-actions {
  white-space: nowrap;
  text-align: center;
}
.wp-risk-badge {
  display: inline-block;
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  text-align: center;
  line-height: 1.4;
}
.wp-risk-badge-high { background-color: #EB5757; }
.wp-risk-badge-mid  { background-color: #F2C94C; color: #5a4500; }
.wp-risk-badge-low  { background-color: #27AE60; }
.dp-reason {
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding: 8px 10px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 4px;
}
.dp-reason-label {
  flex: none;
  font-size: 12px;
  font-weight: 600;
  color: #b26a00;
  margin-right: 10px;
}
.dp-reason .form-control {
  font-size: 12px;
}
.dp-reason-empty {
  border-color: #f2994a;
  background: #fff;
}
.dp-worker-tbl th {
  background: #f7f8fa;
  font-size: 12px;
  padding: 6px 4px;
  text-align: center;
}
.dp-worker-tbl td {
  padding: 3px 6px;
}
.dp-worker-tbl .form-control {
  font-size: 12px;
}
.dp-worker-tbl .dp-sign-cell {
  background: #fcfcfc;
}
</style>
